/**
 * usePageData - 页面数据核心 Hook
 *
 * 负责获取和管理页面配置数据、加载状态和错误处理。
 * 是其他 hooks 的数据基础。
 */

import { ref, computed, type Ref, type ComputedRef } from 'vue-demi';
import type {
  GetPageDataResponse,
  MilestoneRewardItem,
} from '@ark/material-checkin-shared';
import { CurrencyEnum } from '@ark/utils';
import type { MaterialCheckinApiClient } from '../../shared/api';

/**
 * 任务快照数据（简化版，只包含需要的字段）
 */
export interface TaskSnapshot {
  id: string;
  taskName: string;
  lifecycleType: string;
  /** 连续打卡配置（连续模式下有值，累计模式下为 null） */
  streakConfig?: Record<string, unknown> | null;
  /** 里程碑配置 */
  milestoneConfig?: {
    milestones?: Array<{ value: number; label: string }>;
  } | null;
  /** 动作过滤规则（json-logic DSL，包含关联的物料 ID） */
  actionFilter?: Record<string, unknown> | null;
  /** 防重策略（target='action' 为每日打卡任务，target='target_id' 为物料打卡任务） */
  dedupPolicy?: Record<string, unknown> | null;
}

/**
 * usePageData Hook 配置
 */
export interface UsePageDataOptions {
  /** API 客户端实例 */
  apiClient: MaterialCheckinApiClient;
}

/**
 * usePageData Hook 返回值
 */
export interface UsePageDataReturn {
  /** 页面数据 */
  pageData: Ref<GetPageDataResponse | null>;
  /** 是否正在加载 */
  loading: Ref<boolean>;
  /** 错误信息 */
  error: Ref<string>;
  /** 里程碑奖励配置 */
  milestoneRewards: Ref<MilestoneRewardItem[]>;
  /** 里程碑奖励币种 */
  milestoneRewardCurrency: Ref<keyof typeof CurrencyEnum.KV>;
  /** 任务配置列表 */
  tasks: ComputedRef<TaskSnapshot[]>;
  /** 是否有里程碑奖励配置 */
  hasMilestoneRewardConfig: ComputedRef<boolean>;
  /** 加载页面数据 */
  fetchPageData: () => Promise<void>;
}

export interface MilestoneRewardConfigResult {
  rewards: MilestoneRewardItem[];
  currency: keyof typeof CurrencyEnum.KV;
}

/**
 * 从物料配置中提取里程碑奖励配置
 * 查找 componentType 为 'milestone_reward_config' 的物料
 */
export function extractMilestoneRewardConfig(
  materials: Array<{
    componentType: string;
    displayConfig: Record<string, unknown>;
  }>,
): MilestoneRewardConfigResult {
  for (const material of materials) {
    if (material.componentType === 'milestone_reward_config') {
      const rewards = material.displayConfig.rewards as
        | MilestoneRewardItem[]
        | undefined;
      const currency =
        (material.displayConfig.currency as
          | keyof typeof CurrencyEnum.KV
          | undefined) || CurrencyEnum.KV.USD;
      if (rewards && rewards.length > 0) {
        console.log('[usePageData] 找到里程碑奖励配置:', rewards);
        return { rewards, currency };
      }
    }
  }
  console.log('[usePageData] 未找到里程碑奖励配置');
  return { rewards: [], currency: CurrencyEnum.KV.USD };
}

/**
 * 页面数据核心 Hook
 *
 * @example
 * ```typescript
 * const apiClient = new MaterialCheckinApiClient(actPlaybookId);
 * const {
 *   pageData,
 *   loading,
 *   error,
 *   milestoneRewards,
 *   tasks,
 *   hasMilestoneRewardConfig,
 *   fetchPageData,
 * } = usePageData({ apiClient });
 *
 * onMounted(() => {
 *   fetchPageData();
 * });
 * ```
 */
export function usePageData(options: UsePageDataOptions): UsePageDataReturn {
  const { apiClient } = options;

  const pageData = ref<GetPageDataResponse | null>(null);
  const loading = ref(false);
  const error = ref('');
  const milestoneRewards = ref<MilestoneRewardItem[]>([]);
  const milestoneRewardCurrency = ref<string | undefined>(undefined);

  /**
   * 任务配置列表
   */
  const tasks = computed<TaskSnapshot[]>(() => {
    if (!pageData.value?.configSnapshot?.tasks) return [];
    return pageData.value.configSnapshot.tasks as TaskSnapshot[];
  });

  /**
   * 是否有里程碑奖励配置
   */
  const hasMilestoneRewardConfig = computed(() => {
    return milestoneRewards.value.length > 0;
  });

  /**
   * 加载页面数据
   */
  async function fetchPageData(): Promise<void> {
    loading.value = true;
    error.value = '';

    try {
      console.log('[usePageData] 开始加载页面数据');
      const data = await apiClient.getPageData();
      pageData.value = data;
      console.log('[usePageData] 页面数据加载成功');

      // 从物料配置中提取里程碑奖励配置
      if (data.configSnapshot?.materials) {
        const { rewards, currency } = extractMilestoneRewardConfig(
          data.configSnapshot.materials,
        );
        milestoneRewards.value = rewards;
        milestoneRewardCurrency.value = currency;
      }
    } catch (err) {
      console.error('[usePageData] 加载失败:', err);
      error.value = err instanceof Error ? err.message : '加载页面数据失败';
    } finally {
      loading.value = false;
    }
  }

  return {
    pageData,
    loading,
    error,
    milestoneRewards,
    milestoneRewardCurrency,
    tasks,
    hasMilestoneRewardConfig,
    fetchPageData,
  };
}

export default usePageData;
